import * as React from "react";
import { toast } from "react-toastify";

import { Table } from "src/library/components/Tables";
import { ITableInstance } from "src/library/components/Tables/Table";
import { LikeToDo } from "src/library/components/LikeToDo";
import {
  ActionButton,
  Button
} from "src/library/components/Button";
import { PageProps } from "src/pages/Router/routes";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import {
  IPromotion,
  usePromotionsApi,
  PromotionsRequestName
} from "src/modules/promotions/api/promotions.api";
import {
  FilterParams,
  PaginatedData,
  PaginatedParams
} from "src/core/shared/api";
import { promotionTableColumns } from "src/core/shared/components/tables";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";

/**
 * Promotions overview page
 */
const PromotionsOverviewPage: React.ReactFCWithChildren<PageProps<unknown>> = ({ title, ...props }) => {
  // const { history, terminology } = props;
  const TableRef = React.useRef<ITableInstance<IPromotion>>(null);
  const [ isLoading, setIsLoading ] = React.useState(false);
  const [ promotionsData, setPromotionsData ] = React.useState<PaginatedData<IPromotion[]>>();
  const [ listPromotionsRes, listPromotionsReq ] = usePromotionsApi(PromotionsRequestName.LIST_PROMOTIONS);
  const [ deletePromotionRes, deletePromotionReq ] = usePromotionsApi(PromotionsRequestName.DELETE_PROMOTION);
  const checkPermission = useCheckUserPermission();
  const canAmendPromo = checkPermission("amend-promotion");
  const canDeletePromo = checkPermission("delete-promotion");
  const canViewPromo = checkPermission("view-promotion");
  const canCreatePromo = checkPermission("create-promotion");

  /**
   * Table actions
   */
  const tableActions = React.useCallback((row: any) => {
    const actions = [];

    if (canAmendPromo) {
      actions.push({
        title: "Edit promotion",
        action: () => props.history.push(`/admin/promotions/edit/${row.original.id}`)
      });
    }

    if (canDeletePromo) {
      actions.push({
        title: "Delete promotion",
        action: () => deletePromotionReq({ id: row.original.id })
      });
    }

    if (!actions.length && canViewPromo) {
      actions.push({
        title: "View",
        action: () => props.history.push(`/admin/promotions/edit/${row.original.id}`)
      });
    }

    return actions;
  }, [
    canAmendPromo,
    canDeletePromo,
    canViewPromo,
    deletePromotionReq,
    props.history
  ]);

  /**
   * Like to do buttons
   */
  const LikeToDoButtons = React.useMemo((): JSX.Element[] => {
    const buttons = [
      <Button
        key="create-promotion"
        onClick={() => props.history.push("/admin/promotions/create")}
      >
        Create new promotion
      </Button>
    ];

    return buttons;
  }, []);

  /**
   * Fetches the table data
   */
  const onFetchData = React.useCallback((filters: FilterParams<IPromotion>, pagination: PaginatedParams<IPromotion>) => {
    // Update the isLoading state
    setIsLoading(true);

    // Fetch the data
    listPromotionsReq({
      filters,
      pagination
    });
  }, []);

  // On listPromotionsRes change
  React.useEffect(() => {
    // If an error occurs, output it
    if (listPromotionsRes.error) {
      // Update the isLoading state
      setIsLoading(false);
      // Show the error message
      toast.error(listPromotionsRes.error.message);
    }

    // On success
    if (listPromotionsRes.data) {
      // Update the isLoading state
      setIsLoading(false);
      // Update the promotionsData state
      setPromotionsData(listPromotionsRes.data);
    }
  }, [ listPromotionsRes.error, listPromotionsRes.data ]);

  // On deletePromotionRes change
  React.useEffect(() => {
    // If an error occurs, output it
    if (deletePromotionRes.error) {
      // Update the isLoading state
      setIsLoading(false);
      toast.error(deletePromotionRes.error.message);
    }

    // On success
    if (deletePromotionRes.data) {
      /// Update the isLoading state
      setIsLoading(false);

      // Reload the table
      if (TableRef.current) {
        TableRef.current.reloadTable();
      }
    }
  }, [ deletePromotionRes.error, deletePromotionRes.data ]);

  return (
    <Layout
      title={title}
      {...props}
    >
      <MainContentWrapper
        fill
        Header={canCreatePromo && (
          <LikeToDo
            copy="What would you like to do?"
            buttons={LikeToDoButtons}
          />
        )}
      >
        <Table
          tableId="promotions"
          ref={TableRef}
          loading={isLoading}
          tableOf="Promotions"
          view="list"
          viewAllowedToToggle
          data={promotionsData ? promotionsData.result : []}
          // error={listPromotionsRes.error}
          onFetchData={onFetchData}
          // defaultFilters={defaultFilters}
          // defaultPagination={defaultPagination}
          pages={promotionsData ? promotionsData.pages : 0}
          totalRows={promotionsData ? promotionsData.count : 0}
          columns={promotionTableColumns({
            ActionButton: row => {
              const actions = tableActions(row);

              return actions.length ? (
                <ActionButton
                  title="actions"
                  items={tableActions(row)}
                />
              ) : null;
            }
          })}
        />
      </MainContentWrapper>
    </Layout>
  );
};

export default PromotionsOverviewPage;
